import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, CheckCircle, FileText, Settings, Phone, User, Clock, MapPin } from 'lucide-react';

export default function WebsiteBookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = user?.role === 'manager';
  const isRegionalManager = user?.role === 'regional_manager';

  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];

  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState({ total: 0, bills_added: 0, bills_missing: 0 });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [rematching, setRematching] = useState(false);

  const defaultStartDate = isManager ? yesterday : (isRegionalManager ? sevenDaysAgo : yesterday);

  // Filters
  const [filters, setFilters] = useState({
    start_date: defaultStartDate,
    end_date: today,
    branch_id: '',
    bill_status: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    start_date: defaultStartDate,
    end_date: today,
    branch_id: '',
    bill_status: '',
  });

  // Settings dialog
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ email: '', password: '' });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (user) fetchBranches();
  }, [user]);

  useEffect(() => {
    fetchBookings();
    fetchSummary();
  }, [appliedFilters]);

  const fetchBranches = async () => {
    try {
      const res = await api.get('/branches');
      let branchList = res.data;
      // Filter branches for regional_manager to only show assigned branches
      if (isRegionalManager && user?.assigned_branches?.length > 0) {
        branchList = branchList.filter(b => user.assigned_branches.includes(b.id));
      }
      setBranches(branchList);
    } catch { /* ignore */ }
  };

  const buildParams = useCallback(() => {
    const params = {};
    if (!isManager) {
      if (appliedFilters.start_date) params.start_date = appliedFilters.start_date;
      if (appliedFilters.end_date) params.end_date = appliedFilters.end_date;
      if (appliedFilters.branch_id) params.branch_id = appliedFilters.branch_id;
    }
    if (appliedFilters.bill_status) params.bill_status = appliedFilters.bill_status;
    return params;
  }, [appliedFilters, isManager]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/website-bookings', { params: buildParams() });
      setBookings(res.data);
    } catch (err) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await api.get('/website-bookings/summary', { params: buildParams() });
      setSummary(res.data);
    } catch { /* ignore */ }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const payload = {
        start_date: isManager ? yesterday : (appliedFilters.start_date || defaultStartDate),
        end_date: isManager ? today : (appliedFilters.end_date || today),
      };
      const res = await api.post('/website-bookings/sync', payload);
      toast.success(res.data.message);
      fetchBookings();
      fetchSummary();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Sync failed';
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleRematchAll = async () => {
    setRematching(true);
    try {
      const payload = {
        start_date: appliedFilters.start_date || yesterday,
        end_date: appliedFilters.end_date || today,
      };
      const res = await api.post('/website-bookings/rematch-all', payload);
      toast.success(res.data.message);
      fetchBookings();
      fetchSummary();
    } catch (err) {
      toast.error('Re-match failed');
    } finally {
      setRematching(false);
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const handleResetFilters = () => {
    const defaultFilters = { start_date: defaultStartDate, end_date: today, branch_id: '', bill_status: '' };
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const handleAddBill = (booking) => {
    // Navigate to AddBill with booking data as query params
    const params = new URLSearchParams({
      from_booking: booking.booking_id,
      date: booking.booking_date,
      branch_id: booking.branch_id || '',
      theater: booking.theater,
      time_slot: booking.time_slot,
      customer_name: booking.customer_name,
      customer_phone: booking.phone_number,
      advance_paid: booking.advance_amount?.toString() || '0',
    });
    navigate(`/add-bill?${params.toString()}`);
  };

  // Settings handlers
  const openSettings = async () => {
    try {
      const res = await api.get('/website-bookings/settings');
      setSettingsForm({ email: res.data.email || '', password: res.data.password || '' });
    } catch { /* ignore */ }
    setShowSettings(true);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.post('/website-bookings/settings', settingsForm);
      toast.success('Settings saved successfully');
      setShowSettings(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-IN').format(amount);
  };

  return (
    <div className="space-y-6" data-testid="website-bookings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" data-testid="page-title">Update Bills</h2>
          <p className="text-sm text-slate-500 mt-1">
            {isManager ? "Today's & Yesterday's paid bookings from website" : isRegionalManager ? "Past 7 days paid bookings for your branches" : 'Reconcile website bookings with billing'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="sync-bookings-btn"
            onClick={handleSync}
            disabled={syncing}
            className="bg-indigo-600 hover:bg-indigo-700 gap-2"
          >
            <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing...' : 'Sync Bookings'}
          </Button>
          <Button
            data-testid="rematch-btn"
            onClick={handleRematchAll}
            disabled={rematching}
            variant="outline"
            className="gap-2"
          >
            <CheckCircle size={16} />
            {rematching ? 'Checking...' : 'Re-check Bills'}
          </Button>
          {user?.role === 'admin' && (
            <Button
              data-testid="settings-btn"
              onClick={openSettings}
              variant="outline"
              size="icon"
            >
              <Settings size={16} />
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="total-bookings-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Bookings</p>
                <p className="text-3xl font-bold text-slate-900">{summary.total}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center">
                <FileText className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="bills-added-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Bills Added</p>
                <p className="text-3xl font-bold text-emerald-600">{summary.bills_added}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="bills-missing-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Bills Missing</p>
                <p className="text-3xl font-bold text-red-600">{summary.bills_missing}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters - only show date/branch for Admin/Auditor */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            {!isManager && (
              <>
                <div className="w-40">
                  <Label className="text-xs">Start Date</Label>
                  <Input
                    data-testid="filter-start-date"
                    type="date"
                    value={filters.start_date}
                    onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))}
                    min={isRegionalManager ? sevenDaysAgo : undefined}
                  />
                </div>
                <div className="w-40">
                  <Label className="text-xs">End Date</Label>
                  <Input
                    data-testid="filter-end-date"
                    type="date"
                    value={filters.end_date}
                    onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))}
                    max={today}
                  />
                </div>
                <div className="w-52">
                  <Label className="text-xs">Branch</Label>
                  <Select
                    value={filters.branch_id}
                    onValueChange={v => setFilters(f => ({ ...f, branch_id: v === 'all' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="filter-branch">
                      <SelectValue placeholder="All Branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="w-40">
              <Label className="text-xs">Bill Status</Label>
              <Select
                value={filters.bill_status}
                onValueChange={v => setFilters(f => ({ ...f, bill_status: v === 'all' ? '' : v }))}
              >
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="missing">Missing</SelectItem>
                  <SelectItem value="added">Added</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="apply-filters-btn" onClick={handleApplyFilters} className="bg-indigo-600 hover:bg-indigo-700">
              Apply Filters
            </Button>
            <Button data-testid="reset-filters-btn" onClick={handleResetFilters} variant="outline">
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="h-8 w-8 animate-spin text-indigo-500" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <FileText size={48} className="mb-4" />
              <p className="text-lg font-medium">No bookings found</p>
              <p className="text-sm mt-1">Click "Sync Bookings" to fetch from the website</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Booking ID</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold">Customer</TableHead>
                    <TableHead className="font-semibold">Theater</TableHead>
                    <TableHead className="font-semibold">Time Slot</TableHead>
                    <TableHead className="font-semibold">Branch</TableHead>
                    <TableHead className="font-semibold text-right">Advance</TableHead>
                    <TableHead className="font-semibold text-right">Total</TableHead>
                    <TableHead className="font-semibold text-center">Bill Status</TableHead>
                    <TableHead className="font-semibold text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow
                      key={booking.booking_id}
                      data-testid={`booking-row-${booking.booking_id}`}
                      className={booking.bill_status === 'missing' ? 'bg-red-50/40' : ''}
                    >
                      <TableCell className="font-mono text-xs">{booking.booking_id}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(booking.booking_date)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium flex items-center gap-1">
                            <User size={12} className="text-slate-400" />
                            {booking.customer_name}
                          </span>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Phone size={10} className="text-slate-400" />
                            {booking.phone_number}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{booking.theater}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-sm">
                          <Clock size={12} className="text-slate-400" />
                          {booking.time_slot}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-sm">
                          <MapPin size={12} className="text-slate-400" />
                          {booking.location}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatAmount(booking.advance_amount)}</TableCell>
                      <TableCell className="text-right font-medium">{formatAmount(booking.total_amount)}</TableCell>
                      <TableCell className="text-center">
                        {booking.bill_status === 'added' ? (
                          <Badge data-testid={`status-${booking.booking_id}`} className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                            Bill Added
                          </Badge>
                        ) : (
                          <Badge data-testid={`status-${booking.booking_id}`} variant="destructive" className="bg-red-100 text-red-700 hover:bg-red-100">
                            Bill Missing
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {booking.bill_status === 'missing' && booking.branch_id && (
                          <Button
                            data-testid={`add-bill-${booking.booking_id}`}
                            size="sm"
                            onClick={() => handleAddBill(booking)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-xs h-8"
                          >
                            <FileText size={12} className="mr-1" />
                            Add Bill
                          </Button>
                        )}
                        {booking.bill_status === 'missing' && !booking.branch_id && (
                          <span className="text-xs text-slate-400">Branch not mapped</span>
                        )}
                        {booking.bill_status === 'added' && (
                          <span className="text-xs text-emerald-600">Done</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Admin Panel Credentials</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Enter the login credentials for thehappyscreens.in admin panel. These are used to sync bookings.
            </p>
            <div>
              <Label>Email</Label>
              <Input
                data-testid="settings-email"
                type="email"
                value={settingsForm.email}
                onChange={e => setSettingsForm(f => ({ ...f, email: e.target.value }))}
                placeholder="admin@example.com"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                data-testid="settings-password"
                type="password"
                value={settingsForm.password}
                onChange={e => setSettingsForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Enter password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button
              data-testid="save-settings-btn"
              onClick={saveSettings}
              disabled={savingSettings}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
